import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Person,
  Logout,
  AccountCircle,
  ArrowBack,
  Assignment,
  Info as InfoIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Search,
  NotificationsNone,
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import Login from './components/Login';
import SearchInterface from './components/SearchInterface';
import Sidebar from './components/Sidebar';
import ProfileModal from './components/ProfileModal';
import InvitationAccept from './components/InvitationAccept';
import MdrAnnexRulesPage from './components/MdrAnnexRulesPage';
import GlobalNotifications from './components/GlobalNotifications';
import AILiteracyDisclaimer from './components/AILiteracyDisclaimer';
import EmbeddingUpload from './components/EmbeddingUpload';
import CollectionManagement from './components/CollectionManagement';
import UserManagement from './components/UserManagement';
import OrganizationManagement from './components/OrganizationManagement';
import FeedbackSystem from './components/FeedbackSystem';
import FormDownloads from './components/FormDownloads';
import AIWorkflowCreator from './components/AIWorkflowCreator';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import UserChats from './components/UserChats';
import RegulationsManagement from './components/RegulationsManagement';
import SharedSessionView from './components/SharedSessionView';
import SharedWorkflowView from './components/SharedWorkflowView';
import ComplianceDashboard from './components/ComplianceDashboard';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import CsvArtifactEditorPage from './components/CsvArtifactEditorPage';

/**
 * Role System:
 * - role === 0: SUPER_ADMIN users - see chat interface + access to admin tools via floating button
 * - role === 10: ENTERPRISE users - see chat interface only, no admin tools
 * - role === 20: Regular users - see chat interface only, no admin tools
 * 
 * All users now see the chat interface directly in the main content area.
 * Only SUPER_ADMIN users can access admin tools.
 */
import { gamoraApi } from './services/api';

import CssBaseline from '@mui/material/CssBaseline';
import {
  showSuccessNotification,
  showErrorNotification,
  showInfoNotification,
  showWarningNotification,
  showNotification,
} from './utils/notifications';

function getUserRole(): number | undefined {
  try {
    // First try to get role from localStorage (set during login)
    const storedRole = localStorage.getItem('gamora_role');
    if (storedRole) {
      const roleNum = parseInt(storedRole);
      if (!isNaN(roleNum)) {
        return roleNum;
      }
    }
    
    // Fallback: try to decode JWT token
    const token = localStorage.getItem('gamora_token');
    if (!token) return undefined;
    
    const decoded = jwtDecode(token) as any;
    if (!decoded || !decoded.role) return undefined;
    
    const roleNum = parseInt(decoded.role);
    if (isNaN(roleNum)) return undefined;
    
    return roleNum;
  } catch (error) {
    return undefined;
  }
}

function isSuperAdmin(): boolean {
  const role = getUserRole();
  // SUPER_ADMIN users have role === 0
  return role === 0;
}

// Standalone page component for AI Disclaimer route
function AIDisclaimerPage() {
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f5f5f5', 
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      p: { xs: 2, sm: 4 },
      pt: { xs: 4, sm: 6 }
    }}>
      <Box sx={{ 
        maxWidth: '900px', 
        width: '100%',
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: 3,
        p: { xs: 3, sm: 4 },
        mb: 4
      }}>
        <AILiteracyDisclaimerContent onClose={() => window.history.back()} />
      </Box>
    </Box>
  );
}

// Extract the content from the Dialog component for standalone page use
function AILiteracyDisclaimerContent({ onClose }: { onClose: () => void }) {
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #eee'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#1976d2', fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            AI Literacy Note & Disclaimer
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Box sx={{ maxWidth: '100%' }}>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Box sx={{ 
          bgcolor: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: 1, 
          p: 2, 
          mb: 3,
          display: 'flex',
          gap: 1
        }}>
          <WarningIcon sx={{ color: '#ff9800', mt: 0.5 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#856404' }}>
              Important: AI-Generated Content
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404', lineHeight: 1.6 }}>
              The responses provided by Gamura are generated using artificial intelligence. While we strive for accuracy, 
              AI-generated content may contain errors, inaccuracies, or outdated information. Always verify critical 
              information with official sources and consult qualified professionals for regulatory compliance matters.
            </Typography>
          </Box>
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', mt: 3 }}>
          1. Understanding AI Technology
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
          Gamura uses advanced AI language models to analyze documents and generate responses. This technology:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Processes information from uploaded documents and regulatory databases
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Generates responses based on patterns learned from training data
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • May not always distinguish between verified facts and general knowledge
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Can make mistakes, including factual errors, outdated information, or misinterpretations
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
          2. Limitations of AI Responses
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
          AI-generated responses have inherent limitations:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • <strong>Not Legal or Regulatory Advice:</strong> Responses are informational only and do not constitute legal, 
          regulatory, or professional advice. Always consult qualified professionals for compliance matters.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • <strong>Accuracy Not Guaranteed:</strong> While we use reliable sources, AI may generate incorrect, incomplete, 
          or outdated information. Verify all critical information independently.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • <strong>Context Limitations:</strong> AI may not fully understand complex regulatory contexts or jurisdiction-specific 
          nuances. Responses should be reviewed by subject matter experts.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • <strong>No Real-Time Updates:</strong> Information may not reflect the most recent regulatory changes or updates.
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • <strong>Potential Bias:</strong> AI systems may reflect biases present in training data or source materials.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
          3. Best Practices for Using AI-Generated Content
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Verify information with official regulatory sources and documentation
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Consult qualified regulatory professionals for compliance decisions
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Cross-reference AI responses with multiple authoritative sources
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Review and validate all information before making regulatory or business decisions
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Use AI responses as a starting point for research, not as final answers
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Report any errors or concerns through the feedback system
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
          4. Regulatory Compliance Disclaimer
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
          <strong>Important:</strong> Gamura is a tool designed to assist with regulatory research and documentation. 
          It is not a substitute for professional regulatory, legal, or compliance advice. Users are solely responsible for:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Ensuring compliance with all applicable regulations and standards
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Verifying the accuracy and currency of all information used for compliance purposes
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Consulting with qualified professionals before making regulatory submissions or compliance decisions
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
          • Any consequences resulting from reliance on AI-generated content
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ 
          bgcolor: '#e3f2fd', 
          border: '1px solid #2196f3', 
          borderRadius: 1, 
          p: 2, 
          mb: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>
            Your Responsibility
          </Typography>
          <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
            By using Gamura, you acknowledge that you understand the limitations of AI technology and agree to use 
            the service responsibly. You are responsible for validating all information and seeking appropriate professional 
            guidance when needed.
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', mt: 2 }}>
          For questions or concerns about AI-generated content, please contact our support team or use the feedback 
          feature to report issues.
        </Typography>
      </Box>
      
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} variant="contained" color="primary" size="large">
          I Understand
        </Button>
      </Box>
    </Box>
  );
}

function App() {
  // Make notification functions available globally for testing
  React.useEffect(() => {
    (window as any).showSuccessNotification = showSuccessNotification;
    (window as any).showErrorNotification = showErrorNotification;
    (window as any).showInfoNotification = showInfoNotification;
    (window as any).showWarningNotification = showWarningNotification;
    (window as any).showNotification = showNotification;
  }, []);
  
  return (
    <Router>
      <Routes>
        <Route path="/invitations/:token" element={<InvitationAccept />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancelled" element={<PaymentCancelled />} />
        <Route path="/ai-disclaimer" element={<AIDisclaimerPage />} />
        <Route path="/ai-literacy" element={<AIDisclaimerPage />} />
        <Route path="/mdr-annex-rules" element={<MdrAnnexRulesPage />} />
        <Route path="/artifacts/editor" element={<CsvArtifactEditorPage />} />
        <Route path="/shared/:shareToken" element={<SharedSessionView />} />
        <Route path="/shared-workflow/:shareToken" element={<SharedWorkflowView />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

function MainApp() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('gamora_token'));
  const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(
    localStorage.getItem('gamora_session_id') || undefined
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [showAIDisclaimer, setShowAIDisclaimer] = useState(false);
  const [showAdminTools, setShowAdminTools] = useState(false);
  const [adminView, setAdminView] = useState<string | null>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | undefined>(undefined);
  const [showFormsDialog, setShowFormsDialog] = useState(false);
  const [showWorkflowCreator, setShowWorkflowCreator] = useState(false);


  const handleSessionCreated = (sessionId: string) => {
    // Set the session ID - sidebar will not auto-refresh
    setSelectedSessionId(sessionId);
  };

  const handleSessionSelect = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    // Clear workflow selection and admin view when selecting a chat
    setSelectedWorkflowId(undefined);
    setAdminView(null);
    if (sessionId) {
      localStorage.setItem('gamora_session_id', sessionId);
      // Update the URL with the session_id as a query parameter
      const url = `/chat/history?session_id=${sessionId}`;
      window.history.pushState({}, '', url);
    } else {
      // For empty session (new chat), clear localStorage and URL
      localStorage.removeItem('gamora_session_id');
      window.history.pushState({}, '', '/');
    }
  };

  const handleWorkflowSelect = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    // Clear session selection when selecting a workflow
    setSelectedSessionId(undefined);
    localStorage.removeItem('gamora_session_id');
    // Clear admin view to show workflow in main content (SearchInterface handles workflow display)
    setAdminView(null);
    window.history.pushState({}, '', '/');
  };

  const handleNewChat = () => {
    // Clear the selected session to trigger a new chat
    setSelectedSessionId(undefined);
    localStorage.removeItem('gamora_session_id');
    // Clear the URL
    window.history.pushState({}, '', '/');
    // Clear admin view and workflow selection when starting new chat
    setAdminView(null);
    setSelectedWorkflowId(undefined);
  };

  const handleNewWorkflow = () => {
    // Clear the selected workflow to trigger a new workflow creation
    setSelectedWorkflowId(undefined);
    // Clear session selection when creating new workflow
    setSelectedSessionId(undefined);
    localStorage.removeItem('gamora_session_id');
    // Clear the URL
    window.history.pushState({}, '', '/');
    // Clear admin view when starting new workflow
    setAdminView(null);
    // Open the workflow creation dialog
    setShowWorkflowCreator(true);
  };

  const handleLogin = () => setIsLoggedIn(true);
  const handleLogout = () => {
    localStorage.removeItem('gamora_token');
    localStorage.removeItem('gamora_role');
    localStorage.removeItem('gamora_user_id');
    localStorage.removeItem('gamora_session_id');
    setIsLoggedIn(false);
    setSelectedSessionId(undefined);
    // Clear admin view on logout
    setAdminView(null);
    // Clear the URL and redirect to home page
    window.history.pushState({}, '', '/');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePasswordSubmit = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    try {
      await gamoraApi.updatePassword(currentPassword, newPassword);
      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error: any) {
      setPasswordError(error.response?.data?.detail || 'Failed to update password');
    }
  };

  const handleAdminToolSelect = (tool: string) => {
    setAdminView(tool);
    setShowAdminTools(false);
  };

  const handleBackToChat = () => {
    setAdminView(null);
    setSelectedSessionId(undefined);
    setSelectedWorkflowId(undefined);
  };

  return (
    <>
      {/* Main Layout */}
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {isLoggedIn && <Sidebar onSessionSelect={handleSessionSelect} selectedSessionId={selectedSessionId} onNewChat={handleNewChat} onWorkflowSelect={handleWorkflowSelect} selectedWorkflowId={selectedWorkflowId} onFormsClick={() => setShowFormsDialog(true)} onNewWorkflow={handleNewWorkflow} />}
        <Box sx={{ flexGrow: 1, bgcolor: '#f5f6f8', minHeight: '100vh' }}>
          <CssBaseline />
          {isLoggedIn && (
          <AppBar position="static" sx={{ bgcolor: '#fff', color: '#111827', boxShadow: 'none', borderBottom: '1px solid #eceef1' }}>
            <Toolbar sx={{ minHeight: 56, px: 3, gap: 1.5 }}>
              <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '1rem', letterSpacing: '-0.02em' }}>
                Gamura
              </Typography>

              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Search chats, workflows, devices, regulations">
                <IconButton sx={{ color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6', color: '#111827' } }}>
                  <Search sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <IconButton sx={{ color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6', color: '#111827' } }}>
                <NotificationsNone sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton onClick={handleMenuOpen} sx={{ p: 0, ml: 0.5 }}>
                <AccountCircle sx={{ fontSize: 30, color: '#9ca3af' }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 0.75 }}
                disableScrollLock={false}
                keepMounted={false}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    minWidth: 180,
                    borderRadius: '12px',
                    border: '1px solid #eceef1',
                    boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)',
                    py: 0.5,
                    overflow: 'hidden',
                  },
                }}
              >
                <MenuItem
                  onClick={() => { setShowProfile(true); handleMenuClose(); }}
                  sx={{
                    px: 2, py: 1, mx: 0.5, borderRadius: '8px', gap: 1.25,
                    fontSize: '0.82rem', color: '#374151',
                    '&:hover': { bgcolor: '#f8f9fb' },
                  }}
                >
                  <Person sx={{ fontSize: 17, color: '#9ca3af' }} />
                  Profile
                </MenuItem>
                <MenuItem
                  onClick={() => { setShowAIDisclaimer(true); handleMenuClose(); }}
                  sx={{
                    px: 2, py: 1, mx: 0.5, borderRadius: '8px', gap: 1.25,
                    fontSize: '0.82rem', color: '#374151',
                    '&:hover': { bgcolor: '#f8f9fb' },
                  }}
                >
                  <Assignment sx={{ fontSize: 17, color: '#9ca3af' }} />
                  AI Literacy
                </MenuItem>
                <Divider sx={{ my: 0.5, borderColor: '#f3f4f6' }} />
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    px: 2, py: 1, mx: 0.5, borderRadius: '8px', gap: 1.25,
                    fontSize: '0.82rem', color: '#ef4444',
                    '&:hover': { bgcolor: '#fef2f2' },
                  }}
                >
                  <Logout sx={{ fontSize: 17, color: '#ef4444' }} />
                  Log out
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
          )}
          
          {/* Main Content Area */}
          <Box sx={{ 
            flexGrow: 1, 
            p: 0, 
            width: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: isLoggedIn ? 'calc(100vh - 56px)' : '100vh',
            overflow: 'hidden'
          }}>
            {!isLoggedIn ? (
              <Login onLogin={handleLogin} />
            ) : (
              <>
                {/* Admin tools for SUPER_ADMIN users */}
                {isSuperAdmin() && (
                  <Box sx={{ position: 'fixed', bottom: 80, right: 28, zIndex: 1000 }}>
                    <Tooltip title="Admin" placement="left">
                      <IconButton
                        onClick={() => setShowAdminTools(true)}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: '#111827',
                          color: '#fff',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          transition: 'all 0.2s ease',
                          '&:hover': { bgcolor: '#1f2937', boxShadow: '0 6px 20px rgba(0,0,0,0.2)', transform: 'translateY(-1px)' },
                        }}
                      >
                        <Person sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}


                {/* Show admin view or chat interface based on selection */}
                {adminView ? (
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}>
                    {/* Admin View Header */}
                    <Box sx={{
                      px: 3,
                      py: 1.5,
                      bgcolor: '#fff',
                      borderBottom: '1px solid #eceef1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexShrink: 0,
                    }}>
                      <IconButton
                        onClick={handleBackToChat}
                        size="small"
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          color: '#6b7280',
                          '&:hover': { bgcolor: '#f3f4f6', color: '#111827' },
                        }}
                      >
                        <ArrowBack sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>
                        {adminView === 'embed' && 'Document Upload'}
                        {adminView === 'collection' && 'Collections'}
                        {adminView === 'users' && 'Users'}
                        {adminView === 'organizations' && 'Organizations'}
                        {adminView === 'feedback' && 'Feedback'}
                        {adminView === 'chats' && 'User Chats'}
                        {adminView === 'regulations' && 'Regulations'}
                        {adminView === 'compliance' && 'Compliance'}
                      </Typography>
                    </Box>

                    <Box sx={{
                      flex: 1,
                      p: { xs: 2, sm: 3 },
                      overflow: 'auto',
                      minHeight: 0,
                      bgcolor: '#fafbfc',
                    }}>
                      {adminView === 'embed' && <EmbeddingUpload />}
                      {adminView === 'collection' && <CollectionManagement />}
                      {adminView === 'users' && <UserManagement />}
                      {adminView === 'organizations' && <OrganizationManagement />}
                      {adminView === 'feedback' && <FeedbackSystem />}
                      {adminView === 'chats' && <UserChats />}
                      {adminView === 'regulations' && <RegulationsManagement />}
                      {adminView === 'compliance' && <ComplianceDashboard />}
                    </Box>
                  </Box>
                ) : (
                  /* All logged-in users see the chat interface directly */
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%', 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <SearchInterface 
                      sessionId={selectedSessionId} 
                      onSessionCreated={handleSessionCreated}
                      selectedWorkflowId={selectedWorkflowId}
                      onWorkflowSelect={handleWorkflowSelect}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Profile Modal */}
      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />

      {/* AI Literacy Disclaimer Modal */}
      <AILiteracyDisclaimer open={showAIDisclaimer} onClose={() => setShowAIDisclaimer(false)} />

      {/* Global Notifications */}
      <GlobalNotifications />

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {passwordSuccess}
              </Alert>
            )}
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!currentPassword || !newPassword || !confirmPassword}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Tools Modal */}
      <Dialog
        open={showAdminTools}
        onClose={() => setShowAdminTools(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            maxWidth: 520,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 0.5 }}>
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em' }}>
              Admin
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af', mt: 0.25 }}>
              Manage your platform
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowAdminTools(false)}
            size="small"
            sx={{ color: '#9ca3af', width: 32, height: 32, '&:hover': { color: '#111827', bgcolor: '#f3f4f6' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Grid */}
        <Box sx={{ px: 3, pt: 2, pb: 3, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
          {([
            { key: 'embed',         label: 'Documents',     desc: 'Upload & embed',     icon: '↑' },
            { key: 'collection',    label: 'Collections',   desc: 'Manage sets',        icon: '◫' },
            { key: 'users',         label: 'Users',         desc: 'Accounts',           icon: '⏣' },
            { key: 'organizations', label: 'Organizations', desc: 'Teams & orgs',       icon: '⊞' },
            { key: 'feedback',      label: 'Feedback',      desc: 'Analytics',          icon: '◉' },
            { key: 'chats',         label: 'Chats',         desc: 'User sessions',      icon: '⊡' },
            { key: 'forms',         label: 'Forms',         desc: 'Templates',          icon: '▤' },
            { key: 'regulations',   label: 'Regulations',   desc: 'Master data',        icon: '§' },
            { key: 'compliance',    label: 'Compliance',    desc: 'Workflows & more',   icon: '✓' },
          ] as const).map((item) => (
            <Box
              key={item.key}
              onClick={() => item.key === 'forms' ? setShowFormsDialog(true) : handleAdminToolSelect(item.key)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 2,
                px: 1,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                bgcolor: 'transparent',
                border: '1px solid transparent',
                '&:hover': {
                  bgcolor: '#f8f9fb',
                  border: '1px solid #e5e7eb',
                },
                '&:active': {
                  bgcolor: '#f0f1f3',
                  transform: 'scale(0.98)',
                },
              }}
            >
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                color: '#374151',
                mb: 0.25,
              }}>
                {item.icon}
              </Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', lineHeight: 1.2 }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Dialog>

      {/* Forms Dialog */}
      <Dialog
        open={showFormsDialog}
        onClose={() => setShowFormsDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Forms & Templates</Typography>
            <IconButton onClick={() => setShowFormsDialog(false)}>
              ×
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <FormDownloads open={showFormsDialog} onClose={() => setShowFormsDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* AI Workflow Creator Dialog */}
      <AIWorkflowCreator
        open={showWorkflowCreator}
        onClose={() => setShowWorkflowCreator(false)}
        onWorkflowCreated={(workflow) => {
          setShowWorkflowCreator(false);
          if (workflow?.id) {
            setSelectedWorkflowId(workflow.id);
          }
        }}
      />
    </>
  );
}

export default App; 
